import {
  getOvernightUpdate as sdkGetOvernightUpdate,
  saveOvernightUpdate as sdkSaveOvernightUpdate,
  type OvernightUpdateDto,
  type SaveOvernightUpdateDto,
} from "~/lib/api";
import type { OvernightUpdateFormValues } from "~/session/overnight-update/schema";

/**
 * Network + mapping layer for the overnight-update form (V2-E8.1, HAN-68). Keeps
 * the generated SDK and the form-values ↔ wire-payload translation in one place
 * so the screen stays free of API shape details. The SDK functions are aliased
 * to avoid colliding with the wrappers exported here.
 */

/** Form values → the save request body. A blank notes field is omitted. */
export function toOvernightPayload(values: OvernightUpdateFormValues): SaveOvernightUpdateDto {
  const notes = values.dayTeamNotes.trim();
  return {
    whatHappened: values.whatHappened,
    whatWasDone: values.whatWasDone,
    dayTeamNotes: notes === "" ? undefined : notes,
  };
}

/** A saved overnight update → form values, for hydrating the form. */
export function toOvernightFormValues(update: OvernightUpdateDto): OvernightUpdateFormValues {
  return {
    whatHappened: update.whatHappened,
    whatWasDone: update.whatWasDone,
    dayTeamNotes: update.dayTeamNotes ?? "",
  };
}

export type FetchOvernightResult =
  | { status: "ok"; update: OvernightUpdateDto | null }
  | { status: "not-found" }
  | { status: "error"; message: string };

export type SaveOvernightResult =
  | { status: "ok"; update: OvernightUpdateDto }
  | { status: "forbidden" }
  | { status: "not-found" }
  | { status: "error"; message: string };

const GENERIC_SAVE_ERROR = "Something went wrong saving the overnight update. Please try again.";

/**
 * The saved overnight update for a patient (`GET …/overnight-update`). A 200 with
 * `update: null` means none has been saved yet — the form starts blank. 404 →
 * the patient/session is gone; the caller renders not-found.
 */
export async function fetchOvernightUpdate(
  sessionId: string,
  patientId: string,
): Promise<FetchOvernightResult> {
  const result = await sdkGetOvernightUpdate({ path: { sessionId, patientId } });
  if (result.response.status === 404) return { status: "not-found" };
  if (result.error || !result.data) {
    return {
      status: "error",
      message: `Couldn't load the overnight update (${result.response.status}).`,
    };
  }
  return { status: "ok", update: result.data.update };
}

/**
 * Save (upsert) the overnight update for a patient (`PUT …/overnight-update`).
 * 403 → caller isn't the incoming doctor; 404 → patient/session gone; anything
 * else is a generic, retryable error.
 */
export async function saveOvernightUpdate(
  sessionId: string,
  patientId: string,
  values: OvernightUpdateFormValues,
): Promise<SaveOvernightResult> {
  try {
    const result = await sdkSaveOvernightUpdate({
      path: { sessionId, patientId },
      body: toOvernightPayload(values),
    });
    if (result.data && !result.error) return { status: "ok", update: result.data };
    switch (result.response.status) {
      case 403:
        return { status: "forbidden" };
      case 404:
        return { status: "not-found" };
      default:
        return { status: "error", message: GENERIC_SAVE_ERROR };
    }
  } catch {
    return { status: "error", message: GENERIC_SAVE_ERROR };
  }
}
